// Borde de lectura de las ventas del backoffice. El silo devuelve el DTO casi 1:1 con la pantalla,
// salvo el medio de cobro: viaja en el vocabulario del wire (`WIRE`) y aquí se habla de `SPEI`.
import type { PaymentMethod } from '@/domains/sales/wizard/types';
import type { BackofficeSale, SaleLine, SaleLinePromotion } from './contract';
import { toLineProduct, toPaymentMethod, type LineProductWire } from './sessionMapper';

/** Línea vendida como viaja: enums sin acotar y la ficha opcional. */
export interface SaleLineWire extends SaleLinePromotion {
    lineId: string;
    sku: string;
    qty: number;
    basePriceCents: number;
    paymentType: string;
    components: { sku: string; qty: number; identifiers: string[] }[];
    product?: LineProductWire | null;
    financedCents?: number;
    discountCents?: number;
}

/** La venta como viaja: `paymentMethod` sin acotar, y ausente en las ventas anteriores al campo. */
export type BackofficeSaleWire = Omit<BackofficeSale, 'paymentMethod' | 'lines'> & {
    paymentMethod?: string | null;
    lines?: SaleLineWire[] | null;
};

/** Línea vendida al modelo de la pantalla. Una forma de pago desconocida cae a contado. */
export function toSaleLine(l: SaleLineWire): SaleLine {
    return {
        lineId: l.lineId,
        sku: l.sku,
        qty: l.qty,
        basePriceCents: l.basePriceCents,
        paymentType: l.paymentType === 'CREDIT' ? 'CREDIT' : 'CASH',
        components: l.components.map((c) => ({
            sku: c.sku,
            qty: c.qty,
            identifiers: [...c.identifiers],
        })),
        product: toLineProduct(l.product),
        ...(l.financedCents !== undefined ? { financedCents: l.financedCents } : {}),
        ...(l.discountCents !== undefined ? { discountCents: l.discountCents } : {}),
        ...linePromotionOf(l),
    };
}

/** Solo los campos de Promociones que la línea trae; un ausente no aparece. */
function linePromotionOf(l: SaleLinePromotion): SaleLinePromotion {
    return {
        ...(l.role !== undefined && { role: l.role }),
        ...(l.linkedLineId !== undefined && { linkedLineId: l.linkedLineId }),
        ...(l.promotionId !== undefined && { promotionId: l.promotionId }),
        ...(l.promotionVersion !== undefined && { promotionVersion: l.promotionVersion }),
        ...(l.discountCode !== undefined && { discountCode: l.discountCode }),
        ...(l.originalPriceCents !== undefined && { originalPriceCents: l.originalPriceCents }),
        ...(l.promotionDiscounts !== undefined && { promotionDiscounts: l.promotionDiscounts }),
        ...(l.codeDiscountCents !== undefined && { codeDiscountCents: l.codeDiscountCents }),
    };
}

/**
 * Normaliza el medio de cobro (`null` si la venta no lo trae: no se cae a "efectivo") y las líneas.
 * Una venta anterior a las líneas queda sin `lines` ni `skus`.
 */
export function toBackofficeSale(wire: BackofficeSaleWire): BackofficeSale {
    const { lines, skus, ...rest } = wire;
    const method: PaymentMethod | null =
        wire.paymentMethod == null ? null : toPaymentMethod(wire.paymentMethod);
    return {
        ...rest,
        paymentMethod: method,
        ...(lines ? { lines: lines.map(toSaleLine) } : {}),
        ...(skus ? { skus } : {}),
        inventoryReturnPending: wire.inventoryReturnPending === true,
    };
}
